/**
 * AI Tool Names - All tools available for AI assistants.
 *
 * Category-specific lists (ASSISTANT_TOOL_NAMES, etc.) are kept here for
 * use in contexts that don't need the full contract machinery.
 *
 * For type-safe tool inputs, import ToolInputSchemas from ./chat.ts:
 * @example
 * ```typescript
 * import type { ToolInputSchemas } from '@event-manager/shared-types'
 *
 * const scheduleInput: ToolInputSchemas['scheduleEvent'] = {
 *   user_name: 'John Doe',
 *   eventDateTime: '2026-01-20T10:00:00',
 *   event_type: 'CHECK_UP'
 * }
 * ```
 */

export type { ContractToolName as ToolName } from "./tool-contracts";

/**
 * Assistant tool names - tools available to the user-facing chatbot.
 */
export const ASSISTANT_TOOL_NAMES = [
	"getCurrentDatetime",
	"searchDocs",
	"getDocumentSchemas",
	"queryStructuredData",
	"sendBusinessLocation",
	"getUserData",
	"getAvailableTimeSlots",
	"searchAvailableAppointments",
	"scheduleEvent",
	"joinWaitlist",
	"modifyEvent",
	"cancelEvent",
	"sendInteractiveButtons",
	"sendInteractiveList",
	"requestPhoneNumber",
	"handoffToHuman",
	"saveFeedback",
	"collectLead",
] as const;

export type AssistantToolName = (typeof ASSISTANT_TOOL_NAMES)[number];

/**
 * System-agent tool names - tools for batch operations and admin.
 */
export const SYSTEM_AGENT_TOOL_NAMES = [
	"scheduleEvents",
	"modifyEvents",
	"modifyEventRange",
	"cancelEvents",
	"cancelEventRange",
	"getEventSnapshots",
	"getAvailableSlotsBatch",
	"summarizeConversation",
	"getAnalytics",
	"getUsageBreakdown",
	"getKnowledgeGaps",
	"renderChart",
	"manageKnowledgeSource",
	"ingestUploadedFile",
	"triggerRetraining",
	"getAgentSettings",
	"getContactSnapshot",
	"manageContact",
	"manageBeneficiary",
	"queryConversations",
	"searchConversations",
	"getConversationMetrics",
	"manageAutomation",
	"startIntegrationConnection",
	"getChannelStatus",
	"generateInsightsReport",
] as const;

export type SystemAgentToolName = (typeof SYSTEM_AGENT_TOOL_NAMES)[number];

/**
 * Shared tool names - tools available to both assistant and system agent.
 */
export const SHARED_TOOL_NAMES = [
	"getCurrentDatetime",
	"searchDocs",
	"getDocumentSchemas",
	"queryStructuredData",
] as const;

export type SharedToolName = (typeof SHARED_TOOL_NAMES)[number];

/**
 * Scheduling tool names - tools that require the calendar/scheduling feature.
 * These are disabled and non-enableable for orgs on the chat plan.
 */
export const SCHEDULING_TOOL_NAMES = [
	// Assistant scheduling tools
	"getUserData",
	"getAvailableTimeSlots",
	"searchAvailableAppointments",
	"scheduleEvent",
	"joinWaitlist",
	"modifyEvent",
	"cancelEvent",
	// System-agent scheduling tools (batch)
	"scheduleEvents",
	"modifyEvents",
	"modifyEventRange",
	"cancelEvents",
	"cancelEventRange",
	"getEventSnapshots",
	"getAvailableSlotsBatch",
] as const;

export type SchedulingToolName = (typeof SCHEDULING_TOOL_NAMES)[number];

/** Check if a tool name is a scheduling tool */
export function isSchedulingTool(name: string): name is SchedulingToolName {
	return SCHEDULING_TOOL_NAMES.includes(name as SchedulingToolName);
}

/**
 * Opt-in tools — OFF until the operator turns them on, unlike every other tool
 * which a fresh org starts with enabled.
 *
 * Each one is inert or actively wrong without configuration the operator has to
 * supply first: `collectLead` needs a lead-field schema (with no fields the tool
 * factory returns null, so an "enabled" tool is never exposed to the LLM, and
 * enabling it with no fields is itself a save-blocking issue), and the
 * language-backed tools need a display language picked from the org's available
 * languages.
 *
 * MIRRORS the server's `AppConfig.default()` tools array
 * (`server/src/domain/entities/app-config/app-config.entity.ts`), which is the
 * source of truth for a fresh org's config. The web reads this set when merging
 * the live tool registry with an org's saved tools, so a tool the org has never
 * seen lands on the same default the server would have given it.
 */
export const DEFAULT_DISABLED_TOOL_NAMES = [
	"saveFeedback",
	"summarizeConversation",
	"collectLead",
] as const;

export type DefaultDisabledToolName =
	(typeof DEFAULT_DISABLED_TOOL_NAMES)[number];

const DEFAULT_DISABLED_TOOL_NAME_SET: ReadonlySet<string> = new Set(
	DEFAULT_DISABLED_TOOL_NAMES
);

/** Whether a fresh org starts with this tool enabled. */
export function isToolEnabledByDefault(name: string): boolean {
	return !DEFAULT_DISABLED_TOOL_NAME_SET.has(name);
}

/**
 * The customer assistant's scheduling family — gated as one unit by the
 * org-config `schedulingToolsEnabled` master (per-tool choices stay stored).
 */
export const ASSISTANT_SCHEDULING_TOOL_NAMES = [
	"getUserData",
	"getAvailableTimeSlots",
	"searchAvailableAppointments",
	"scheduleEvent",
	"joinWaitlist",
	"modifyEvent",
	"cancelEvent",
] as const;

/**
 * The document-retrieval (RAG) bundle — configured as ONE toggle in the AI
 * tools UI and gated at runtime by the org-config `ragToolsEnabled` master.
 * `readSection` piggybacks on `searchDocs` in the assembler and needs no
 * entry here.
 */
export const RAG_RETRIEVAL_TOOL_NAMES = [
	"searchDocs",
	"getDocumentSchemas",
	"queryStructuredData",
] as const;

/**
 * Tools with no config switch: force-enabled for every agent at runtime and
 * hidden from the config UI. Current datetime is a hard dependency of
 * scheduling (and harmless everywhere else).
 */
export const ALWAYS_ON_TOOL_NAMES = ["getCurrentDatetime"] as const;
